import { getApiUrl } from "@/utils/api";
import { useState } from "react";
import { FileDown, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = getApiUrl();

const PERIOD_OPTIONS = [
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
  { value: "all", label: "All Time" },
];

async function downloadFile(url, filename, mimeType) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(new Blob([blob], { type: mimeType }));
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export default function ExportPanel() {
  const [period, setPeriod] = useState("30d");
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const exportCSV = async () => {
    setCsvLoading(true);
    try {
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      await downloadFile(
        `${API}/analytics/export/csv?period=${period}`,
        `analytics_${period}_${dateStr}.csv`,
        "text/csv"
      );
      toast.success("CSV exported successfully");
    } catch {
      toast.error("CSV export failed");
    } finally {
      setCsvLoading(false);
    }
  };

  const exportPDF = async () => {
    setPdfLoading(true);
    try {
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      await downloadFile(
        `${API}/analytics/export/pdf?period=${period}`,
        `analytics_report_${period}_${dateStr}.pdf`,
        "application/pdf"
      );
      toast.success("PDF report downloaded");
    } catch {
      toast.error("PDF export failed");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="analytics-export">
      <div>
        <h3 className="font-serif text-base font-medium">Export Reports</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Download your analytics data in CSV or PDF format</p>
      </div>

      {/* Period selector */}
      <div className="bg-white border border-border rounded-lg p-5 space-y-4">
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Select Period</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  period === p.value ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Export cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* CSV */}
        <div className="bg-white border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-emerald-100 rounded-lg">
              <FileText className="h-5 w-5 text-emerald-700" />
            </div>
            <div>
              <p className="text-sm font-semibold">CSV Data Export</p>
              <p className="text-xs text-muted-foreground">Raw event data for analysis</p>
            </div>
          </div>
          <ul className="text-xs text-muted-foreground space-y-1 mb-4 ml-1">
            <li>• All analytics events with full metadata</li>
            <li>• Page URLs, device types, traffic sources</li>
            <li>• Compatible with Excel, Google Sheets</li>
          </ul>
          <Button
            onClick={exportCSV}
            disabled={csvLoading}
            variant="outline"
            className="w-full rounded-none text-xs"
            data-testid="export-csv-btn"
          >
            {csvLoading ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Exporting…</>
            ) : (
              <><FileDown className="h-3.5 w-3.5 mr-1.5" /> Download CSV</>
            )}
          </Button>
        </div>

        {/* PDF */}
        <div className="bg-white border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-rose-100 rounded-lg">
              <FileDown className="h-5 w-5 text-rose-700" />
            </div>
            <div>
              <p className="text-sm font-semibold">PDF Summary Report</p>
              <p className="text-xs text-muted-foreground">Branded report ready to share</p>
            </div>
          </div>
          <ul className="text-xs text-muted-foreground space-y-1 mb-4 ml-1">
            <li>• Executive summary with KPIs</li>
            <li>• Top pages and property performance</li>
            <li>• Ready to share with clients or stakeholders</li>
          </ul>
          <Button
            onClick={exportPDF}
            disabled={pdfLoading}
            className="w-full rounded-none text-xs"
            data-testid="export-pdf-btn"
          >
            {pdfLoading ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generating…</>
            ) : (
              <><FileDown className="h-3.5 w-3.5 mr-1.5" /> Download PDF Report</>
            )}
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Exports are tenant-scoped and contain only your data.
      </p>
    </div>
  );
}
